import {
  LocationType,
  hashCodeLib,
  searchForm as searchFormState,
} from 'shared';
import type { MappedLocation, RouteState } from 'shared';
import type { Dict } from '@aviata/platform-types';

import { sentryModel } from '@/shared/model';
import { isRoundTripAsTwoStoredLegs } from '@/shared/lib';

import { lastSearch } from '../model';

const buildMappedLocationFromDictCity = (
  city: Dict.Cities.City,
): MappedLocation => ({
  type: LocationType.CITY,
  code: city.iata_code,
  name: city.name,
  name_ru: city.name_ru,
  name_en: city.name_en,
  name_kk: city.name_kk,
  country: {
    type: LocationType.COUNTRY,
    code: city.country.iso_code,
    name: city.country.name,
    name_ru: city.country.name_ru,
    name_en: city.country.name_en,
    name_kk: city.country.name_kk,
  },
});

const cityCode = (iata: string) => iata.slice(0, 3);

const isRoundTripPair = (
  first: { dep: string; arr: string },
  second: { dep: string; arr: string },
) =>
  cityCode(first.dep) === cityCode(second.arr) &&
  cityCode(first.arr) === cityCode(second.dep);

/**
 * Returns `true` when the existing `lastSearch` already stores the same
 * round-trip pair as two separate multi-trip legs.  In that case we must
 * keep the multi-leg layout so the form re-hydrates in multi-trip mode.
 */
const existingLastSearchIsMultiLegRoundTrip = (): boolean => {
  const existing = lastSearch.getSavedSearchParams();
  if (!existing) {
    return false;
  }
  return isRoundTripAsTwoStoredLegs(existing.routes as RouteState[]);
};

/**
 * Reconstructs `lastSearch` routes from a search hash code.
 *
 * - 1 itinerary  → one route with one date (one-way).
 * - 2 itineraries that mirror each other (A→B / B→A) → one route with two
 *   dates (round-trip representation that the simple form expects), **unless**
 *   the existing `lastSearch` already stores this pair as two multi-trip legs
 *   — in that case two separate routes are kept so the form stays in
 *   multi-trip mode.
 * - Anything else (including 2-leg "true" multi-city) → one route per
 *   itinerary so the multi-trip form stays in multi-trip mode.
 */
export const saveLastSearchFromDict = (
  dict: Dict.Entity,
  hashCode: string,
): void => {
  try {
    const params = hashCodeLib.getSearchParams(hashCode);

    const resolveCity = (iata: string) =>
      dict.cities[iata] ?? dict.airports[iata]?.city;

    const buildLocation = (iata: string): MappedLocation | null => {
      const city = resolveCity(iata);
      return city ? buildMappedLocationFromDictCity(city) : null;
    };

    const { itineraries } = params;
    let routes: RouteState[];

    const isRoundTrip =
      itineraries.length === 2 &&
      isRoundTripPair(itineraries[0], itineraries[1]);

    const collapseToSimpleRoundTrip =
      isRoundTrip && !existingLastSearchIsMultiLegRoundTrip();

    if (collapseToSimpleRoundTrip || itineraries.length === 1) {
      const [first, second] = itineraries;
      const dep = buildLocation(first.dep);
      const arr = buildLocation(first.arr);
      if (!dep || !arr) {
        return;
      }
      const dates = second ? [first.date, second.date] : [first.date];
      routes = searchFormState.lib.buildInitialRoutes([{ dep, arr, dates }]);
    } else {
      const partials: Array<{
        dep: MappedLocation;
        arr: MappedLocation;
        dates: string[];
      }> = [];
      for (const itinerary of itineraries) {
        const dep = buildLocation(itinerary.dep);
        const arr = buildLocation(itinerary.arr);
        if (!dep || !arr) {
          return;
        }
        partials.push({ dep, arr, dates: [itinerary.date] });
      }
      routes = searchFormState.lib.buildInitialRoutes(partials);
    }

    lastSearch.saveSearchParams({
      routes,
      passengers: params.passengers,
      cabinClass: params.cabinClass,
      isYouth: params.isYouth,
    });
  } catch (e) {
    sentryModel.captureException(
      sentryModel.getReadableError('ERR_SAVE_LAST_SEARCH', e as Error),
    );
  }
};
